import { toJS } from '@gonfalon/legacy-immutable/immutableUtils';

import { createClause } from '#utils/clause/records/Clause';
import type { ExperimentRollout } from '#utils/flags/records/Rollout';
import { createExperimentRollout } from '#utils/flags/records/Rollout';

import { AutomatedReleaseInstructionKind } from '../automated-release/types';
import { ClauseInstructionKind } from '../clauses/constants';
import { makeExpiringTargetSemanticInstruction } from '../expiringTargets/helpers';
import { ExpiringTargetsInstructionKind } from '../expiringTargets/types';
import { MembersAndTeamsInstructionKind } from '../membersAndTeams/types';
import { OnOffInstructionKind } from '../onOff/types';
import { FlagPrerequisitesInstructionKind } from '../prerequisites/types';
import { RuleInstructionKind } from '../rules/constants';
import { createAggregateRule } from '../rules/createAggregateRule';
import { TargetsInstructionKind } from '../targets/types';
import { makeAddUserTargetsInstruction } from '../userTargets/helpers';
import { UserTargetsInstructionKind } from '../userTargets/types';
import type { VariationSemanticInstruction } from '../variations/types';
import { FlagVariationsInstructionKind } from '../variations/types';

import type {
  AllFlagInstructionKinds,
  AllSupportedInstructionKinds,
  AllSupportedInstructions,
  SemanticInstruction,
} from './types';

const makeSemanticInstrucionForKind = {
  [OnOffInstructionKind.TURN_FLAG_ON]: () => ({
    kind: OnOffInstructionKind.TURN_FLAG_ON,
  }),
  [OnOffInstructionKind.TURN_FLAG_OFF]: () => ({
    kind: OnOffInstructionKind.TURN_FLAG_OFF,
  }),
  [OnOffInstructionKind.ENABLE_TARGETING]: () => ({
    kind: OnOffInstructionKind.ENABLE_TARGETING,
  }),
  [OnOffInstructionKind.DISABLE_TARGETING]: () => ({
    kind: OnOffInstructionKind.DISABLE_TARGETING,
  }),
  [OnOffInstructionKind.UPDATE_OFF_VARIATION]: (instructionProps) => ({
    variationId: null,
    ...toJS(instructionProps),
    kind: OnOffInstructionKind.UPDATE_OFF_VARIATION,
  }),
  [RuleInstructionKind.ADD_RULE]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return {
      ref: '',
      ...jsInstructionProps,
      clauses: jsInstructionProps.clauses.map(createClause),
      ruleId: jsInstructionProps._key,
      kind: RuleInstructionKind.ADD_RULE,
    };
  },
  [RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return {
      ref: '',
      ...jsInstructionProps,
      clauses: jsInstructionProps.clauses.map(createClause),
      ruleId: jsInstructionProps._key,
      kind: RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2,
    };
  },
  [RuleInstructionKind.REMOVE_RULE]: (instructionProps) => ({
    ruleId: '',
    ...toJS(instructionProps),
    kind: RuleInstructionKind.REMOVE_RULE,
  }),
  [RuleInstructionKind.REORDER_RULES]: (instructionProps) => ({
    ruleIds: [],
    ...toJS(instructionProps),
    kind: RuleInstructionKind.REORDER_RULES,
  }),
  [RuleInstructionKind.REPLACE_RULES]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return {
      ...jsInstructionProps,
      rules: jsInstructionProps.rules.map(createAggregateRule),
      kind: RuleInstructionKind.REPLACE_RULES,
    };
  },
  [RuleInstructionKind.UPDATE_RULE_DESCRIPTION]: (instructionProps) => ({
    ruleId: '',
    description: '',
    ...toJS(instructionProps),
    kind: RuleInstructionKind.UPDATE_RULE_DESCRIPTION,
  }),
  [ClauseInstructionKind.ADD_VALUES_TO_CLAUSE]: (instructionProps) => ({
    ruleId: '',
    clauseId: '',
    values: [],
    ...toJS(instructionProps),
    kind: ClauseInstructionKind.ADD_VALUES_TO_CLAUSE,
  }),
  [ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE]: (instructionProps) => ({
    ruleId: '',
    clauseId: '',
    values: [],
    ...toJS(instructionProps),
    kind: ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE,
  }),
  [ClauseInstructionKind.ADD_CLAUSES]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);

    if ('clauses' in jsInstructionProps && Array.isArray(jsInstructionProps.clauses)) {
      jsInstructionProps.clauses = jsInstructionProps.clauses.map((c: unknown) => toJS(c));
    }

    return {
      ruleId: '',
      clauses: [],
      ...jsInstructionProps,
      kind: ClauseInstructionKind.ADD_CLAUSES,
    };
  },
  [ClauseInstructionKind.REMOVE_CLAUSES]: (instructionProps) => ({
    ruleId: '',
    clauseIds: [],
    ...toJS(instructionProps),
    kind: ClauseInstructionKind.REMOVE_CLAUSES,
  }),
  [ClauseInstructionKind.UPDATE_CLAUSE]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);

    return {
      ruleId: '',
      clauseId: '',
      ...jsInstructionProps,
      clause: jsInstructionProps.clause ? createClause(jsInstructionProps.clause) : null,
      kind: ClauseInstructionKind.UPDATE_CLAUSE,
    };
  },
  [RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return {
      ruleId: '',
      ...jsInstructionProps,
      kind: RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE,
    };
  },
  [RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    if (typeof jsInstructionProps === 'object') {
      Object.entries(jsInstructionProps).forEach(([key, value]) => {
        jsInstructionProps[key] = toJS(value);
      });
    }

    let ruleExpAlloc: ExperimentRollout | undefined = jsInstructionProps.experimentAllocation;
    if (ruleExpAlloc) {
      ruleExpAlloc = createExperimentRollout(ruleExpAlloc);
      return {
        ...jsInstructionProps,
        experimentAllocation: ruleExpAlloc,
        kind: RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT,
      };
    }
    return {
      ...jsInstructionProps,
      kind: RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT,
    };
  },
  [RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2,
  }),
  [OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return {
      ...jsInstructionProps,
      kind: OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH,
    };
  },
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);

    return {
      contextKind: undefined,
      rolloutWeights: undefined,
      rolloutBucketBy: undefined,
      rolloutContextKind: undefined,
      variationId: undefined,
      ...jsInstructionProps,
      experimentAllocation: jsInstructionProps.experimentAllocation
        ? createExperimentRollout(jsInstructionProps.experimentAllocation)
        : undefined,
      kind: OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT,
    };
  },
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2,
  }),
  [UserTargetsInstructionKind.ADD_USER_TARGETS]: (instructionProps) => {
    const jsInstructionProps = toJS(instructionProps);
    return makeAddUserTargetsInstruction(jsInstructionProps.values ?? [], jsInstructionProps.variationId || '');
  },
  [UserTargetsInstructionKind.REMOVE_USER_TARGETS]: (instructionProps) => ({
    values: [],
    variationId: '',
    ...toJS(instructionProps),
    kind: UserTargetsInstructionKind.REMOVE_USER_TARGETS,
  }),
  [UserTargetsInstructionKind.REPLACE_USER_TARGETS]: (instructionProps) => ({
    targets: [],
    ...toJS(instructionProps),
    kind: UserTargetsInstructionKind.REPLACE_USER_TARGETS,
  }),
  [TargetsInstructionKind.ADD_TARGETS]: (instructionProps) => ({
    contextKind: '',
    values: [],
    variationId: '',
    ...toJS(instructionProps),
    kind: TargetsInstructionKind.ADD_TARGETS,
  }),
  [TargetsInstructionKind.REMOVE_TARGETS]: (instructionProps) => ({
    contextKind: '',
    values: [],
    variationId: '',
    ...toJS(instructionProps),
    kind: TargetsInstructionKind.REMOVE_TARGETS,
  }),
  [TargetsInstructionKind.REPLACE_TARGETS]: (instructionProps) => ({
    targets: [],
    contextKind: '',
    ...toJS(instructionProps),
    kind: TargetsInstructionKind.REPLACE_TARGETS,
  }),
  [MembersAndTeamsInstructionKind.ADD_MEMBERS]: (instructionProps) => ({
    values: [],
    ...toJS(instructionProps),
    kind: MembersAndTeamsInstructionKind.ADD_MEMBERS,
  }),
  [MembersAndTeamsInstructionKind.REMOVE_MEMBERS]: (instructionProps) => ({
    values: [],
    ...toJS(instructionProps),
    kind: MembersAndTeamsInstructionKind.REMOVE_MEMBERS,
  }),
  [MembersAndTeamsInstructionKind.ADD_TEAM_KEYS]: (instructionProps) => ({
    values: [],
    ...toJS(instructionProps),
    kind: MembersAndTeamsInstructionKind.ADD_TEAM_KEYS,
  }),
  [FlagPrerequisitesInstructionKind.ADD_PREREQUISITE]: (instructionProps) => ({
    key: '',
    variationId: '',
    ...toJS(instructionProps),
    kind: FlagPrerequisitesInstructionKind.ADD_PREREQUISITE,
  }),
  [FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE]: (instructionProps) => ({
    key: '',
    ...toJS(instructionProps),
    kind: FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE,
  }),
  [FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE]: (instructionProps) => ({
    key: '',
    variationId: '',
    ...toJS(instructionProps),
    kind: FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE,
  }),
  [FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES]: (instructionProps) => ({
    key: '',
    prerequisites: [],
    ...toJS(instructionProps),
    kind: FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES,
  }),
  [FlagVariationsInstructionKind.ADD_VARIATION]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: FlagVariationsInstructionKind.ADD_VARIATION,
  }),
  [FlagVariationsInstructionKind.UPDATE_VARIATION]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: FlagVariationsInstructionKind.UPDATE_VARIATION,
  }),
  [FlagVariationsInstructionKind.REMOVE_VARIATION]: (instructionProps) => ({
    variationId: '',
    ...toJS(instructionProps),
    kind: FlagVariationsInstructionKind.REMOVE_VARIATION,
  }),
  [FlagVariationsInstructionKind.UPDATE_DEFAULT_VARIATION]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: FlagVariationsInstructionKind.UPDATE_DEFAULT_VARIATION,
  }),
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE]: (instructionProps) =>
    makeExpiringTargetSemanticInstruction(
      ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE,
      toJS(instructionProps),
    ) || instructionProps,
  [AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE,
  }),
  [AutomatedReleaseInstructionKind.ADVANCE_AUTOMATED_RELEASE]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: AutomatedReleaseInstructionKind.ADVANCE_AUTOMATED_RELEASE,
  }),
  [RuleInstructionKind.START_AUTOMATED_RELEASE]: (instructionProps) => ({
    ...toJS(instructionProps),
    kind: RuleInstructionKind.START_AUTOMATED_RELEASE,
  }),
  [RuleInstructionKind.DISABLE_RULE]: (instructionProps) => ({
    ruleId: '',
    ...toJS(instructionProps),
    kind: RuleInstructionKind.DISABLE_RULE,
  }),
  [RuleInstructionKind.ENABLE_RULE]: (instructionProps) => ({
    ruleId: '',
    ...toJS(instructionProps),
    kind: RuleInstructionKind.ENABLE_RULE,
  }),
} as const satisfies {
  // oxlint-disable-next-line typescript/no-explicit-any
  [K in AllSupportedInstructionKinds]: (instructionProps: any) => Extract<AllSupportedInstructions, { kind: K }>;
};

/**
 * Converts an object or Immutable.js Map into the corresponding semantic instruction Immutable.js Record,
 * according to the "kind" provided.
 */
export function makeSemanticInstruction(
  kind: AllFlagInstructionKinds | FlagVariationsInstructionKind,
  instructionProps: $TSFixMe,
): SemanticInstruction | VariationSemanticInstruction {
  return makeSemanticInstrucionForKind[kind]?.(instructionProps) ?? instructionProps;
}
